// hooks/useSettingsData.js
// Centralized data fetching for Settings page
// Fetches summary stats for all sections in parallel

import { useState, useEffect, useCallback } from 'react';
import supabase from '../../../../lib/supabaseClient';

export const useSettingsData = (tenantId) => {
  const [data, setData] = useState({
    tenant: { name: '', industry: '' },
    company: { isComplete: false, summary: '', lastUpdated: null },
    a2p: { status: 'not_started', summary: '', progress: null },
    phones: { total: 0, assigned: 0, summary: '' },
    messaging: { isConfigured: true, replyMode: 'paced', summary: '' },
    team: { total: 0, active: 0, pending: 0, avatars: [], summary: '' },
    webChat: { isConfigured: false, summary: 'Web chat widget' },
    voiceChannel: { isConfigured: false, isVisible: false, summary: 'AI voice agent for inbound calls' },
    messenger: { isConfigured: false, summary: 'AI replies to your Facebook Page messages' }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel
      const [
        tenantResult,
        settingsResult,
        phonesResult,
        a2pBrandResult,
        a2pCampaignsResult,
        teamResult,
        pendingInvitesResult,
        webChatResult,
        voiceChannelResult,
        messengerResult
      ] = await Promise.allSettled([
        // Tenant info
        supabase
          .from('tenants')
          .select('name, industry')
          .eq('id', tenantId)
          .single(),
        
        // Settings (was 'settings', now 'platform_settings')
        supabase
          .from('platform_settings')
          .select('key, value, updated_at')
          .eq('tenant_id', tenantId),
        
        // Phone numbers (removed non-existent columns)
        supabase
          .from('phone_numbers')
          .select('id, phone_number, status, messaging_service_sid, created_at')
          .eq('tenant_id', tenantId),
        
        // A2P Brand (fixed column name: brand_sid -> twilio_brand_sid)
        supabase
          .from('a2p_brands')
          .select('status, twilio_brand_sid, created_at')
          .eq('tenant_id', tenantId)
          .single(),
        
        // A2P Campaigns
        supabase
          .from('a2p_campaigns')
          .select('id, status')
          .eq('tenant_id', tenantId),
        
        // Team members (was 'profiles', now 'users_profile', fixed columns)
        supabase
          .from('users_profile')
          .select('id, first_name, last_name, email, is_active, role')
          .eq('tenant_id', tenantId),
        
        // Pending invites
        supabase
          .from('invitations')
          .select('id')
          .eq('tenant_id', tenantId)
          .eq('status', 'pending'),

        // Web chat widget config
        supabase
          .from('tenants')
          .select('widget_key, widget_campaign_id, web_chat_company_name')
          .eq('id', tenantId)
          .single(),

        // Voice channel config. 🟢 2026-09-13 (Voice Channel Settings redesign):
        // voice_ai_campaign_id added — the section is now visible whenever the
        // tenant is voice-provisioned OR has a designated Voice campaign
        // (either can be true without the other: a tenant can provision the
        // agent for follow-up-only voice with no dedicated line, or Surf can
        // designate a campaign before the tenant ever provisions). See
        // VoiceChannelSection.jsx / VOICE-AI-V1-HANDOFF.md for the full reasoning.
        supabase
          .from('tenants')
          .select('voice_ai_enabled, retell_phone_number, voice_ai_campaign_id')
          .eq('id', tenantId)
          .single(),

        // Messenger channel config. NOT a direct table read: messenger_pages has RLS on with ZERO
        // policies and no anon/authenticated grants (same lockdown as the Twilio token columns), so a
        // browser Supabase call here would just fail. The service-role Express endpoint is the only
        // reader. Wrapped to match the shape of the other Promise.allSettled entries ({data}).
        (async () => {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) return { data: null };
            const base = process.env.REACT_APP_API_URL
              || (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://api.getsurfox.com/api');
            const r = await fetch(`${base}/messenger/page`, { headers: { Authorization: `Bearer ${session.access_token}` } });
            if (!r.ok) return { data: null };
            const body = await r.json();
            return { data: body.connected ? { page_id: body.page_id, page_name: body.page_name, status: body.status } : null };
          } catch (err) {
            console.error('Failed to fetch Messenger status:', err.message);
            return { data: null };
          }
        })()
      ]);

      // Process tenant data
      const tenant = tenantResult.status === 'fulfilled' && tenantResult.value.data
        ? tenantResult.value.data
        : { name: '', industry: '' };

      // Process settings
      const settings = settingsResult.status === 'fulfilled' && settingsResult.value.data
        ? settingsResult.value.data.reduce((acc, s) => ({ ...acc, [s.key]: s }), {})
        : {};

      // Process company profile
      const companyName = settings.company_name?.value;
      const industry = tenant.industry || settings.industry?.value;
      const officeOpen = settings.officeOpenHour?.value;
      const officeClose = settings.officeCloseHour?.value;
      const officeDays = settings.officeDays?.value;
      
      const companyIsComplete = !!(companyName && industry);
      const companySummary = companyIsComplete
        ? `${companyName}${industry ? ` · ${industry}` : ''}${officeOpen && officeClose ? ` · ${formatHours(officeOpen, officeClose, officeDays)}` : ''}`
        : 'Not configured';
      
      const companyLastUpdated = settings.company_name?.updated_at
        ? formatLastUpdated(settings.company_name.updated_at)
        : null;

      // Process phones (updated to use available columns)
      const phones = phonesResult.status === 'fulfilled' && phonesResult.value.data
        ? phonesResult.value.data
        : [];
      const phonesAssigned = phones.filter(p => p.messaging_service_sid).length;
      const phonesSummary = phones.length > 0
        ? `${phones.length} number${phones.length > 1 ? 's' : ''} · ${phonesAssigned} assigned`
        : 'No phone numbers';

      // Process A2P (updated to use twilio_brand_sid)
      const a2pBrand = a2pBrandResult.status === 'fulfilled' && a2pBrandResult.value.data
        ? a2pBrandResult.value.data
        : null;
      const a2pCampaigns = a2pCampaignsResult.status === 'fulfilled' && a2pCampaignsResult.value.data
        ? a2pCampaignsResult.value.data
        : [];
      
      // Normalize to the lowercase values the Settings card consumes
      // (=== 'approved' / 'pending' / 'not_started'). Raw Twilio statuses are
      // uppercase ('VERIFIED'), so passing them through left the card stuck on
      // "Action Required" forever. Registration is done once the brand AND a
      // campaign are verified — number assignment is the Phone Numbers step.
      const a2pRegistered = isA2PApproved(a2pBrand?.status)
        && a2pCampaigns.some(c => isA2PApproved(c.status));
      const a2pStatus = !a2pBrand ? 'not_started' : (a2pRegistered ? 'approved' : 'pending');
      const a2pProgress = getA2PProgress(a2pBrand, a2pCampaigns, phones);
      const a2pSummary = getA2PSummary(a2pBrand, a2pCampaigns, phones);

      // Process messaging settings
      const replyMode = settings.aiReplyMode?.value || 'paced';
      const tcpaEnabled = settings.tcpaEnabled?.value === 'true';
      const messagingSummary = `${replyMode === 'paced' ? 'Paced mode (2-5 min)' : 'Status-based'}${tcpaEnabled ? ' · TCPA enabled' : ''}`;

      // Process team (updated to use is_active instead of status)
      const teamMembers = teamResult.status === 'fulfilled' && teamResult.value.data
        ? teamResult.value.data
        : [];
      const pendingInvites = pendingInvitesResult.status === 'fulfilled' && pendingInvitesResult.value.data
        ? pendingInvitesResult.value.data
        : [];
      
      const activeMembers = teamMembers.filter(m => m.is_active).length;
      const teamAvatars = teamMembers.slice(0, 4).map(m => ({
        initials: getInitials(m.first_name, m.last_name, m.email),
        color: getAvatarColor(m.id)
      }));
      const teamSummary = `${activeMembers} active${pendingInvites.length > 0 ? ` · ${pendingInvites.length} pending` : ''}`;

      // Process web chat
      const webChatTenant = webChatResult.status === 'fulfilled' && webChatResult.value.data
        ? webChatResult.value.data
        : null;
      const webChatConfigured = !!(webChatTenant?.widget_key);
      const webChatSummary = webChatConfigured
        ? `Widget active${webChatTenant.web_chat_company_name ? ` · ${webChatTenant.web_chat_company_name}` : ''}${webChatTenant.widget_campaign_id ? ' · Campaign assigned' : ''}`
        : 'Not installed · Add the widget to your website';

      // Process voice channel. 🟢 2026-09-13 (Voice Channel Settings redesign):
      // the number that matters is the DESIGNATED VOICE CAMPAIGN's own
      // Retell-imported number, never tenants.retell_phone_number (that field
      // is the tenant-level dedicated-line leftover from before campaign
      // numbers existed — see _shared/retellCampaignNumbers.ts's header).
      const voiceChannelTenant = voiceChannelResult.status === 'fulfilled' && voiceChannelResult.value.data
        ? voiceChannelResult.value.data
        : null;
      const fmtPhone = (e) => {
        if (!e) return '';
        const d = e.replace(/\D/g, '');
        return (d.length === 11 && d.startsWith('1'))
          ? `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`
          : e;
      };

      // Visible whenever EITHER is true — the two can diverge (see the
      // fetch's own comment above), and hiding on either flag alone would
      // hide the only place to fix the other.
      const voiceChannelVisible = !!(voiceChannelTenant?.voice_ai_enabled || voiceChannelTenant?.voice_ai_campaign_id);

      let voiceCampaignName = null;
      let voiceCampaignPhone = null;
      let voiceCampaignImported = false;
      if (voiceChannelTenant?.voice_ai_campaign_id) {
        const { data: voiceCampaign } = await supabase
          .from('campaigns')
          .select('id, name, phone_number_id')
          .eq('id', voiceChannelTenant.voice_ai_campaign_id)
          .maybeSingle();
        voiceCampaignName = voiceCampaign?.name || null;
        if (voiceCampaign?.phone_number_id != null) {
          const { data: voicePhone } = await supabase
            .from('phone_numbers')
            .select('phone_number, retell_imported_at')
            .eq('id', voiceCampaign.phone_number_id)
            .maybeSingle();
          voiceCampaignPhone     = voicePhone?.phone_number || null;
          voiceCampaignImported  = !!voicePhone?.retell_imported_at;
        }
      }

      const voiceChannelConfigured = !!(voiceCampaignPhone && voiceCampaignImported);
      const voiceChannelSummary = voiceChannelConfigured
        ? `Active · ${fmtPhone(voiceCampaignPhone)}${voiceCampaignName ? ` · ${voiceCampaignName}` : ''}`
        : voiceChannelTenant?.voice_ai_campaign_id
          ? `Voice campaign designated · number not yet connected to Retell`
          : voiceChannelTenant?.voice_ai_enabled
            ? 'Voice AI provisioned · no dedicated line designated yet'
            : 'Not provisioned · Set up voice AI to enable it';

      // Process Messenger. messenger_pages is one row per tenant (unique); no row = not connected.
      const messengerPage = messengerResult.status === 'fulfilled' && messengerResult.value.data
        ? messengerResult.value.data
        : null;
      const messengerConfigured = !!(messengerPage && messengerPage.status === 'active');
      // The header chip already says Connected / Needs attention / Not Connected, so the subtitle is
      // just the Page name (or the prompt), never a second "Connected · ...".
      const messengerSummary = messengerPage
        ? (messengerPage.page_name || messengerPage.page_id)
        : 'Connect your Facebook Page';

      // Update state
      setData({
        tenant,
        company: {
          isComplete: companyIsComplete,
          summary: companySummary,
          lastUpdated: companyLastUpdated
        },
        a2p: {
          status: a2pStatus,
          summary: a2pSummary,
          progress: a2pProgress,
          isComplete: a2pProgress.isComplete
        },
        phones: {
          total: phones.length,
          assigned: phonesAssigned,
          summary: phonesSummary,
          list: phones
        },
        messaging: {
          isConfigured: true,
          replyMode,
          tcpaEnabled,
          summary: messagingSummary
        },
        team: {
          total: teamMembers.length,
          active: activeMembers,
          pending: pendingInvites.length,
          avatars: teamAvatars,
          summary: teamSummary,
          members: teamMembers
        },
        webChat: {
          isConfigured: webChatConfigured,
          summary: webChatSummary
        },
        voiceChannel: {
          isConfigured: voiceChannelConfigured,
          isVisible: voiceChannelVisible,
          summary: voiceChannelSummary
        },
        messenger: {
          isConfigured: messengerConfigured,
          pageStatus: messengerPage ? messengerPage.status : null,
          summary: messengerSummary
        }
      });

    } catch (err) {
      console.error('Failed to load settings data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatHours(open, close, days) {
  const formatTime = (h) => {
    const hour = parseInt(h);
    if (hour === 0) return '12am';
    if (hour === 12) return '12pm';
    return hour > 12 ? `${hour - 12}pm` : `${hour}am`;
  };
  
  let daysStr = '';
  if (days) {
    const dayList = days.split(',');
    if (dayList.length === 7) daysStr = 'Every day';
    else if (dayList.length === 6 && !dayList.includes('Sunday')) daysStr = 'Mon–Sat';
    else if (dayList.length === 5) daysStr = 'Mon–Fri';
    else daysStr = `${dayList.length} days`;
  }
  
  return `${daysStr} ${formatTime(open)}–${formatTime(close)}`;
}

function formatLastUpdated(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Updated today';
  if (diffDays === 1) return 'Updated yesterday';
  if (diffDays < 7) return `Updated ${diffDays} days ago`;
  if (diffDays < 30) return `Updated ${Math.floor(diffDays / 7)} weeks ago`;
  return `Updated ${Math.floor(diffDays / 30)} months ago`;
}

export function isA2PApproved(status) {
  if (!status) return false;
  const s = status.toLowerCase();
  return s === 'approved' || s === 'twilio-approved' || s === 'verified' || s === 'active';
}

export function getA2PProgress(brand, campaigns, phones) {
  const brandRegistered = !!brand?.twilio_brand_sid;
  const brandVerified = isA2PApproved(brand?.status);
  const campaignVerified = campaigns.some(c => isA2PApproved(c.status));
  const numbersAssigned = phones.some(p => p.messaging_service_sid);

  const steps = [
    {
      label: 'Brand Registered',
      complete: brandRegistered,
      current: !brandRegistered
    },
    {
      label: 'Brand Verified',
      complete: brandVerified,
      current: brandRegistered && !brandVerified
    },
    {
      label: 'Campaign Active',
      complete: campaignVerified,
      current: brandVerified && !campaignVerified
    },
    {
      label: 'Numbers Assigned',
      complete: numbersAssigned,
      current: campaignVerified && !numbersAssigned
    }
  ];

  const completedCount = steps.filter(s => s.complete).length;

  return {
    current: completedCount,
    total: steps.length,
    steps,
    isComplete: completedCount === steps.length
  };
}

function getA2PSummary(brand, campaigns, phones) {
  if (!brand) return 'Registration required · Required for SMS delivery';

  const brandVerified = isA2PApproved(brand.status);
  const verifiedCampaigns = campaigns.filter(c => isA2PApproved(c.status));
  const assignedPhones = phones.filter(p => p.messaging_service_sid);

  if (brandVerified && verifiedCampaigns.length > 0) {
    return `Brand verified · ${verifiedCampaigns.length} campaign${verifiedCampaigns.length !== 1 ? 's' : ''} active · ${assignedPhones.length} number${assignedPhones.length !== 1 ? 's' : ''} assigned`;
  }
  if (brandVerified) return 'Brand verified · Setting up campaigns';
  if (brand.twilio_brand_sid) return 'Brand registration submitted · Under review';
  return 'Registration required · Required for SMS delivery';
}

function getInitials(firstName, lastName, email) {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) {
    return firstName.substring(0, 2).toUpperCase();
  }
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  return '??';
}

function getAvatarColor(id) {
  const colors = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-violet-500',
    'bg-pink-500',
    'bg-amber-500',
    'bg-cyan-500',
    'bg-rose-500',
    'bg-indigo-500'
  ];
  
  // Simple hash based on id
  let hash = 0;
  const str = String(id);
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
}

export default useSettingsData;